import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiSave, HiMail } from 'react-icons/hi';
import { useEffect, useState } from 'react';
import { Button, Input, Card, CardHeader } from '../components/ui';
import api from '../services/api';
import { getErrorMessage } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { Setting } from '../types';

// Settings shown in the generic list are everything except these (handled in dedicated UI).
const HIDDEN_SETTINGS = ['app.name', 'email.sendNotifications'];

interface EmailConfig {
  emailEnabled: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string; // outbound only; the server never returns the password
  emailFrom: string;
  smtpPassSet?: boolean; // from the server: whether a password is currently stored
}

async function getSettings(): Promise<Setting[]> {
  const response = await api.get<Setting[]>('/settings');
  return response.data;
}

async function updateSetting(key: string, value: string): Promise<Setting> {
  const response = await api.put<Setting>(`/settings/${key}`, { value });
  return response.data;
}

async function getEmailConfig(): Promise<EmailConfig> {
  const response = await api.get<EmailConfig>('/settings/email-config');
  return response.data;
}

async function saveEmailConfig(cfg: EmailConfig): Promise<EmailConfig> {
  const response = await api.put<EmailConfig>('/settings/email-config', cfg);
  return response.data;
}

async function sendTestEmail(to: string): Promise<{ success: boolean; message: string }> {
  const response = await api.post<{ success: boolean; message: string }>('/settings/test-email', { to });
  return response.data;
}

function Toggle({ checked, onChange, label, hint }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <span className="block text-sm font-medium text-gray-700">{label}</span>
        {hint && <span className="block text-sm text-gray-500">{hint}</span>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
          checked ? 'bg-primary-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}

export function Settings() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [testTo, setTestTo] = useState(user?.email ?? '');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [emailForm, setEmailForm] = useState<EmailConfig | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  });

  const { data: emailConfig } = useQuery({
    queryKey: ['emailConfig'],
    queryFn: getEmailConfig,
  });

  useEffect(() => {
    // Seed the form; the password always starts empty (server never sends it).
    if (emailConfig) setEmailForm({ ...emailConfig, smtpPass: '' });
  }, [emailConfig]);

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => updateSetting(key, value),
    onSuccess: (_, variables) => {
      toast.success('Gespeichert');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setEditedValues((prev) => {
        const next = { ...prev };
        delete next[variables.key];
        return next;
      });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const emailConfigMutation = useMutation({
    mutationFn: () => saveEmailConfig(emailForm!),
    onSuccess: (cfg) => {
      toast.success('E-Mail-Konfiguration gespeichert (in .env geschrieben)');
      setEmailForm(cfg);
      queryClient.invalidateQueries({ queryKey: ['emailConfig'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const testEmailMutation = useMutation({
    mutationFn: () => sendTestEmail(testTo),
    onSuccess: (result) => {
      setTestResult(result);
      if (result.success) toast.success('Test-E-Mail gesendet');
      else toast.error('Test fehlgeschlagen');
    },
    onError: (error) => {
      setTestResult({ success: false, message: getErrorMessage(error) });
      toast.error(getErrorMessage(error));
    },
  });

  const handleChange = (key: string, value: string) => setEditedValues((prev) => ({ ...prev, [key]: value }));
  const handleSave = (key: string) => {
    const value = editedValues[key];
    if (value !== undefined) updateMutation.mutate({ key, value });
  };
  const getValue = (s: Setting): string => (editedValues[s.key] !== undefined ? editedValues[s.key]! : s.value);
  const hasChanges = (s: Setting): boolean => editedValues[s.key] !== undefined && editedValues[s.key] !== s.value;
  const setEf = (patch: Partial<EmailConfig>) => setEmailForm((prev) => (prev ? { ...prev, ...patch } : prev));

  const notify = settings?.find((s) => s.key === 'email.sendNotifications');
  const notifyOn = notify ? notify.value !== 'false' : true;
  const genericSettings = settings?.filter((s) => !HIDDEN_SETTINGS.includes(s.key)) ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <CardHeader title="Settings" description="Configure system settings" />

      {/* General settings */}
      <Card>
        <div className="space-y-6">
          {genericSettings.map((setting) => (
            <div key={setting.id} className="flex items-start gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{setting.key}</label>
                <Input value={getValue(setting)} onChange={(e) => handleChange(setting.key, e.target.value)} />
                {setting.description && <p className="mt-1 text-sm text-gray-500">{setting.description}</p>}
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="mt-6"
                disabled={!hasChanges(setting)}
                onClick={() => handleSave(setting.key)}
                isLoading={updateMutation.isPending}
              >
                <HiSave className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {genericSettings.length === 0 && (
            <p className="text-gray-500 text-center py-8">No settings configured</p>
          )}
        </div>
      </Card>

      {/* Email configuration */}
      <Card className="mt-6">
        <div className="flex items-center gap-2 mb-1">
          <HiMail className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">E-Mail-Konfiguration</h3>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          SMTP-Zugang für den Mailversand. Wird beim Speichern in die <code>.env</code> geschrieben und sofort
          angewendet (kein Neustart nötig).
        </p>

        {emailForm && (
          <div className="space-y-5">
            <Toggle
              checked={emailForm.emailEnabled}
              onChange={(v) => setEf({ emailEnabled: v })}
              label="E-Mail-Versand aktiv (SMTP)"
              hint="Schaltet den tatsächlichen Versand über SMTP ein/aus (EMAIL_ENABLED)."
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="SMTP Host"
                value={emailForm.smtpHost}
                onChange={(e) => setEf({ smtpHost: e.target.value })}
                placeholder="smtp.example.com"
              />
              <Input
                type="number"
                label="SMTP Port"
                value={emailForm.smtpPort}
                onChange={(e) => setEf({ smtpPort: parseInt(e.target.value) || 0 })}
                placeholder="587"
              />
              <Input
                label="SMTP Benutzer"
                value={emailForm.smtpUser}
                onChange={(e) => setEf({ smtpUser: e.target.value })}
                placeholder="user@example.com"
              />
              <Input
                type="password"
                label="SMTP Passwort"
                value={emailForm.smtpPass}
                onChange={(e) => setEf({ smtpPass: e.target.value })}
                placeholder={emailForm.smtpPassSet ? '•••••••• (gesetzt – leer lassen = unverändert)' : 'noch nicht gesetzt'}
              />
              <Input
                label="Absender (From)"
                value={emailForm.emailFrom}
                onChange={(e) => setEf({ emailFrom: e.target.value })}
                placeholder="noreply@yourdomain.com"
                className="sm:col-span-2"
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={() => emailConfigMutation.mutate()} isLoading={emailConfigMutation.isPending}>
                <HiSave className="w-4 h-4 mr-2" />
                Konfiguration speichern
              </Button>
            </div>
          </div>
        )}

        {/* Notification master switch (stored in settings) */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <Toggle
            checked={notifyOn}
            onChange={(v) => updateMutation.mutate({ key: 'email.sendNotifications', value: v ? 'true' : 'false' })}
            label="Benachrichtigungen senden"
            hint="Aktions-E-Mails (Lizenzen, Anträge) an die Beteiligten senden."
          />
        </div>

        {/* Test sender */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">E-Mail-Konfiguration testen</h4>
          <p className="text-sm text-gray-500 mb-4">
            Sendet eine Test-E-Mail über die aktuell gespeicherten SMTP-Daten und zeigt das Ergebnis bzw. die
            Fehlermeldung.
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-md">
              <Input
                type="email"
                label="Empfänger"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="empfaenger@example.com"
              />
            </div>
            <Button onClick={() => testEmailMutation.mutate()} isLoading={testEmailMutation.isPending} disabled={!testTo}>
              <HiMail className="w-4 h-4 mr-2" />
              Test senden
            </Button>
          </div>
          {testResult && (
            <div
              className={`mt-4 text-sm rounded-lg px-3 py-2 ${
                testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {testResult.success ? '✅ ' : '❌ '}
              {testResult.message}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
